import { useState, useEffect } from 'react';
import { get, post, put, remove, API_URL } from '../../utils/api';

// API URL from environment or default
// const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const MunicipalityManagement = ({ onRefresh }) => {
  const [municipalities, setMunicipalities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentMunicipality, setCurrentMunicipality] = useState(null);
  const [formMode, setFormMode] = useState('create'); // 'create' or 'edit'
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    province: '',
    contactEmail: '',
    contactPhone: '',
    url: '',
    billingDay: '',
    rateChangeMonth: ''
  });

  // Load municipalities on component mount
  useEffect(() => {
    fetchMunicipalities();
  }, []);

  // Fetch municipalities from the API
  const fetchMunicipalities = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await get(`${API_URL}/site/municipalities`);
      setMunicipalities(response.data);
    } catch (err) {
      // console.error('Error fetching municipalities:', err);
      setError('Failed to load municipalities. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Open modal for creating a new municipality
  const handleCreate = () => {
    setFormData({
      name: '',
      code: '',
      province: '',
      contactEmail: '',
      contactPhone: '',
      url: '',
      billingDay: '',
      rateChangeMonth: ''
    });
    setFormMode('create');
    setCurrentMunicipality(null);
    setIsModalOpen(true);
  };

  // Open modal for editing an existing municipality
  const handleEdit = (municipality) => {
    setFormData({
      name: municipality.name,
      code: municipality.code,
      province: municipality.province || '',
      contactEmail: municipality.contactEmail || '',
      contactPhone: municipality.contactPhone || '',
      url: municipality.url || '',
      billingDay: municipality.billingDay?.toString() || '',
      rateChangeMonth: municipality.rateChangeMonth?.toString() || ''
    });
    setFormMode('edit');
    setCurrentMunicipality(municipality);
    setIsModalOpen(true);
  };

  // Submit form to create or update a municipality
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Format the data
      const data = {
        name: formData.name,
        code: formData.code,
        province: formData.province || null,
        contactEmail: formData.contactEmail || null,
        contactPhone: formData.contactPhone || null,
        url: formData.url || null,
        billingDay: formData.billingDay ? parseInt(formData.billingDay) : null,
        rateChangeMonth: formData.rateChangeMonth ? parseInt(formData.rateChangeMonth) : null
      };
      
      if (formMode === 'edit' && currentMunicipality) {
        // Add UUID for updates
        data.uuid = currentMunicipality.uuid;
        
        // Update existing municipality
        await put(`${API_URL}/site/municipalities`, data);
      } else {
        // Create new municipality
        await post(`${API_URL}/site/municipalities`, data);
      }
      
      // Close modal and refresh data
      setIsModalOpen(false);
      fetchMunicipalities();
      if (onRefresh) onRefresh();
    } catch (err) {
      // console.error('Error saving municipality:', err);
      setError('Failed to save municipality. ' + (err.response?.data?.message || 'Please try again later.'));
    }
  };

  // Handle municipality deletion
  const handleDelete = async (municipality) => {
    if (!confirm(`Are you sure you want to delete ${municipality.name}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await remove(`${API_URL}/site/municipalities/${municipality.uuid}`);
      
      // Refresh data
      fetchMunicipalities();
      if (onRefresh) onRefresh();
    } catch (err) {
      // console.error('Error deleting municipality:', err);
      alert('Failed to delete municipality. It may have associated sites that need to be deleted first.');
    }
  };

  if (loading && municipalities.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Municipalities</h2>
        <button
          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
          onClick={handleCreate}
        >
          Add Municipality
        </button>
      </div>
      
      {/* Municipalities Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-2 px-4 text-left text-sm font-medium text-gray-500">Name</th>
              <th className="py-2 px-4 text-left text-sm font-medium text-gray-500">Code</th>
              <th className="py-2 px-4 text-left text-sm font-medium text-gray-500">Province</th>
              <th className="py-2 px-4 text-left text-sm font-medium text-gray-500">Contact Email</th>
              <th className="py-2 px-4 text-left text-sm font-medium text-gray-500">Contact Phone</th>
              <th className="py-2 px-4 text-left text-sm font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {municipalities.length === 0 ? (
              <tr>
                <td colSpan="6" className="py-4 px-4 text-center text-gray-500">
                  No municipalities found. Click "Add Municipality" to create one.
                </td>
              </tr>
            ) : (
              municipalities.map((municipality) => (
                <tr key={municipality.uuid} className="hover:bg-gray-50">
                  <td className="py-2 px-4">{municipality.name}</td>
                  <td className="py-2 px-4">{municipality.code}</td>
                  <td className="py-2 px-4">{municipality.province || '-'}</td>
                  <td className="py-2 px-4">{municipality.contactEmail || '-'}</td>
                  <td className="py-2 px-4">{municipality.contactPhone || '-'}</td>
                  <td className="py-2 px-4">
                    <div className="flex space-x-2">
                      <button
                        className="text-blue-500 hover:text-blue-700"
                        onClick={() => handleEdit(municipality)}
                      >
                        Edit
                      </button>
                      <button
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDelete(municipality)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Municipality Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-screen overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">
              {formMode === 'create' ? 'Add Municipality' : 'Edit Municipality'}
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    required
                    maxLength={10}
                  />
                  <p className="text-xs text-gray-500 mt-1">Max 10 characters. Used as a unique identifier.</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Province
                  </label>
                  <input
                    type="text"
                    name="province"
                    value={formData.province}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Phone
                  </label>
                  <input
                    type="text"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website URL
                  </label>
                  <input
                    type="url"
                    name="url"
                    value={formData.url}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    placeholder="https://example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Billing Day
                  </label>
                  <input
                    type="number"
                    name="billingDay"
                    value={formData.billingDay}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    min="1"
                    max="31"
                  />
                  <p className="text-xs text-gray-500 mt-1">Day of month when billing typically occurs (1-31)</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rate Change Month
                  </label>
                  <select
                    name="rateChangeMonth"
                    value={formData.rateChangeMonth}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  >
                    <option value="">Select month</option>
                    <option value="1">January</option>
                    <option value="2">February</option>
                    <option value="3">March</option>
                    <option value="4">April</option>
                    <option value="5">May</option>
                    <option value="6">June</option>
                    <option value="7">July</option>
                    <option value="8">August</option>
                    <option value="9">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Month when rates typically change</p>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  {formMode === 'create' ? 'Create Municipality' : 'Update Municipality'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MunicipalityManagement; 